/** Stored in BranchRule.comparisonValue when comparisonValueType is Date and the rule uses a rolling "N years/months ago" reference. */
export type RelativeDateComparisonValue = {
  relative: true;
  amount: number;
  unit: "years" | "months";
};

export function isRelativeDateComparisonValue(value: unknown): value is RelativeDateComparisonValue {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const o = value as Record<string, unknown>;
  if (o.relative !== true) {
    return false;
  }
  if (typeof o.amount !== "number" || !Number.isInteger(o.amount) || o.amount < 1) {
    return false;
  }
  return o.unit === "years" || o.unit === "months";
}

import { subtractRelativeFromCalendarToday } from "./enquiryEvaluationTimeZone";

/** Resolves BranchRule date comparison to an ISO date string (yyyy-MM-dd) for operators that compare to a single day. */
export function resolveBranchRuleDateComparisonToIso(
  value: unknown,
  evaluationTimeZoneId: string | null | undefined = undefined
): string | null {
  if (isRelativeDateComparisonValue(value)) {
    return subtractRelativeFromCalendarToday(value.amount, value.unit, evaluationTimeZoneId);
  }
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  return null;
}

export function formatRelativePeriodPhrase(amount: number, unit: "years" | "months"): string {
  if (unit === "years") {
    return amount === 1 ? "1 year" : `${amount} years`;
  }
  return amount === 1 ? "1 month" : `${amount} months`;
}

/** Labels for date ordering when comparing to a rolling "N years/months ago" (GreaterThan = over ago, LessThan = less than ago). */
export function getRelativeDateOrderingOptionLabels(comparisonValue: unknown): { overAgo: string; withinAgo: string } {
  const rel = isRelativeDateComparisonValue(comparisonValue)
    ? comparisonValue
    : { relative: true as const, amount: 1, unit: "years" as const };
  const period = formatRelativePeriodPhrase(rel.amount, rel.unit);
  return {
    overAgo: `Over ${period} ago`,
    withinAgo: `Less than ${period} ago`,
  };
}

/**
 * Summary label for a saved rule: GreaterThan → "Over N years ago", LessThan → "Less than N years ago".
 * Returns null if not a relative date ordering rule.
 */
export function getRelativeDateOrderingRuleSummaryLabel(comparisonType: string, comparisonValue: unknown): string | null {
  if (!isRelativeDateComparisonValue(comparisonValue)) {
    return null;
  }
  if (comparisonType !== "GreaterThan" && comparisonType !== "LessThan") {
    return null;
  }
  const period = formatRelativePeriodPhrase(comparisonValue.amount, comparisonValue.unit);
  return comparisonType === "GreaterThan" ? `Over ${period} ago` : `Less than ${period} ago`;
}

export function formatBranchRuleDateComparisonForDisplay(value: unknown): string {
  if (isRelativeDateComparisonValue(value)) {
    const u = value.unit === "years" ? (value.amount === 1 ? "year" : "years") : value.amount === 1 ? "month" : "months";
    return `${value.amount} ${u} ago`;
  }
  if (typeof value === "string") {
    return value;
  }
  return String(value ?? "");
}
